import { useCallback, useEffect, useRef, useState } from "react";
import { authApi } from "../services/authApi";
import { useAuth } from "../context/AuthContext";

const DISPLAY_TICK_MS = 30_000;
const SERVER_SYNC_MS = 5 * 60_000;

/**
 * The backend extends the session on every authenticated request (see
 * api/security/session_manager.py), but only /api/auth/refresh's response
 * actually reports the resulting expiry -- so this hook polls that
 * endpoint periodically (independent of whatever else the user is doing)
 * just to keep the displayed countdown honest, and to keep the session
 * alive during long stretches of read-only browsing that wouldn't
 * otherwise touch the API.
 */
export function useSessionTimer() {
  const { isAuthenticated } = useAuth();
  const [expiresAt, setExpiresAt] = useState(null);
  const [remainingMs, setRemainingMs] = useState(null);
  const syncTimerRef = useRef(null);
  const tickTimerRef = useRef(null);

  const sync = useCallback(async () => {
    try {
      const { expires_at } = await authApi.refresh();
      setExpiresAt(new Date(expires_at));
    } catch {
      // Refresh failures surface via the 401 interceptor (apiClient.js),
      // which already redirects to login -- nothing extra to do here.
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    sync();
    syncTimerRef.current = setInterval(sync, SERVER_SYNC_MS);
    return () => clearInterval(syncTimerRef.current);
  }, [isAuthenticated, sync]);

  useEffect(() => {
    tickTimerRef.current = setInterval(() => {
      setRemainingMs(expiresAt ? Math.max(0, expiresAt.getTime() - Date.now()) : null);
    }, DISPLAY_TICK_MS);
    setRemainingMs(expiresAt ? Math.max(0, expiresAt.getTime() - Date.now()) : null);
    return () => clearInterval(tickTimerRef.current);
  }, [expiresAt]);

  const label = remainingMs == null ? "--:--" : formatRemaining(remainingMs);

  return { remainingMs, label, expiresAt, refresh: sync };
}

function formatRemaining(ms) {
  const totalMinutes = Math.floor(ms / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  /*
   * DAYS ONCE IT IS DAYS. Reported from a device: a badge reading
   * "Session secure - 719h 58m" straight after signing in. The number was
   * right -- a 30-day session -- and useless: nobody converts 719 hours
   * in their head, and a figure that large next to a padlock reads as a
   * glitch rather than as reassurance.
   *
   * This badge exists to warn that a session is running out, so precision
   * matters at the short end and not at the long one. Below two days it
   * stays exact to the minute, which is where somebody might actually act
   * on it.
   */
  if (hours >= 48) return `${Math.floor(hours / 24)}d`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}
